// نشست حساب دستگاه (US-IAM-053، AP-IAM-05): تبلت جفت‌شده بدون کاربر انسانی. اعتبارنامه Client فقط در کوکی HttpOnly (مسیر
// /api/v1/identity/device-accounts) است؛ توکن مستأجر دستگاه (۵ دقیقه، actor_type = Device) فقط در حافظه نگه داشته و پیش از انقضا تازه
// می‌شود (بدون localStorage — CLAUDE.md §3). همان Context نشست مستأجر را فراهم می‌کند تا صفحه حالت تحویل تبلت بدون تغییر کار کند.
import {
  createPhoenixClient,
  isApiError,
  unwrap,
  type DeviceTokenResponse,
  type PhoenixClient,
  type TenantSession,
} from '@phoenix/api-client';
import { useTranslation } from '@phoenix/i18n';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { TenantSessionContext, type TenantSessionValue } from './TenantSessionProvider';

/** کد خطای اعتبارنامه نامعتبر یا دستگاه لغوشده. */
export const DEVICE_CREDENTIALS_INVALID = 'PHX-IAM-0807';

export type DeviceSessionStatus = 'loading' | 'unpaired' | 'ready' | 'error';

export interface DeviceSessionProviderProps {
  baseUrl?: string;
  fetch?: typeof globalThis.fetch;
  /** نمای هر وضعیت: جفت‌نشده ← فرم کد؛ آماده ← فرزندان. */
  children: (state: { status: DeviceSessionStatus; device: DeviceTokenResponse | null; pair: (code: string) => Promise<unknown>; retry: () => void }) => ReactNode;
}

export function DeviceSessionProvider({ baseUrl = '', fetch: customFetch, children }: DeviceSessionProviderProps) {
  const { i18n } = useTranslation();
  const [status, setStatus] = useState<DeviceSessionStatus>('loading');
  const [device, setDevice] = useState<DeviceTokenResponse | null>(null);
  const tokenRef = useRef<{ token: string; expiresAtMs: number } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const languageRef = useRef(i18n.language);
  languageRef.current = i18n.language;

  const anonymous = useMemo(
    () => createPhoenixClient({ baseUrl, fetch: customFetch, getToken: async () => undefined, getLanguage: () => languageRef.current }),
    [baseUrl, customFetch],
  );

  const refresh = useCallback(async (): Promise<boolean> => {
    try {
      const issued = await unwrap(anonymous.POST('/api/v1/identity/device-accounts:token', { body: { clientId: null, clientSecret: null } }));
      tokenRef.current = { token: issued.accessToken, expiresAtMs: Date.now() + Number(issued.expiresIn) * 1000 };
      setDevice(issued);
      setStatus('ready');
      if (timer.current) {
        clearTimeout(timer.current);
      }
      timer.current = setTimeout(() => void refresh(), Math.max(5_000, Number(issued.expiresIn) * 1000 - 60_000));
      return true;
    } catch (error) {
      tokenRef.current = null;
      setDevice(null);
      setStatus(isApiError(error) && error.code === DEVICE_CREDENTIALS_INVALID ? 'unpaired' : 'error');
      return false;
    }
  }, [anonymous]);

  useEffect(() => {
    void refresh();
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, [refresh]);

  const tenantApi = useMemo<PhoenixClient>(
    () =>
      createPhoenixClient({
        baseUrl,
        fetch: customFetch,
        getLanguage: () => languageRef.current,
        getToken: async (options) => {
          if (options?.forceRefresh || !tokenRef.current || tokenRef.current.expiresAtMs < Date.now() + 10_000) {
            await refresh();
          }
          return tokenRef.current?.token;
        },
        onUnauthorized: () => refresh(),
      }),
    [baseUrl, customFetch, refresh],
  );

  const pair = useCallback(
    async (code: string) => {
      await unwrap(anonymous.POST('/api/v1/identity/device-accounts:pair', { body: { code, useCookie: true } }));
      await refresh();
    },
    [anonymous, refresh],
  );

  const value = useMemo<TenantSessionValue>(() => {
    const session: TenantSession | null = device
      ? {
          accessToken: '',
          activeBranchId: device.branchId,
          branchIds: [device.branchId],
          expiresAt: new Date(tokenRef.current?.expiresAtMs ?? Date.now()).toISOString(),
          expiresIn: device.expiresIn,
          membershipId: device.deviceId,
          tenantId: device.tenantId,
          tokenType: device.tokenType,
        }
      : null;
    const noop = () => undefined;
    return {
      status: device ? 'ready' : 'loading',
      tenants: [],
      session,
      activeTenant: null,
      selectError: null,
      loadError: null,
      epoch: 0,
      keycloakApi: anonymous,
      tenantApi,
      notices: { locked: null, featureDisabled: null },
      dismissFeatureNotice: noop,
      selectTenant: async () => false,
      switchTenant: async () => false,
      switchBranch: async () => false,
      suspend: noop,
      resume: () => refresh(),
      reload: () => void refresh(),
      onReset: () => noop,
    } as TenantSessionValue;
  }, [anonymous, device, refresh, tenantApi]);

  return (
    <TenantSessionContext.Provider value={value}>
      {children({ status, device, pair, retry: () => void refresh() })}
    </TenantSessionContext.Provider>
  );
}
